import classNames from "classnames";
import LystLogo from "img/branding/lyst-logo-2020-black.svg?inline";
import trim from "lodash/trim";
import React from "react";
import { JsonLd } from "react-schemaorg";
import { WebSite } from "schema-dts";
import CartSummary from "web/react/components/app-header/cart-summary/cart-summary";
import HeaderBarSearch from "web/react/components/app-header/header-bar-search/header-bar-search";
import HeaderGenders from "web/react/components/app-header/header-genders/header-genders";
import UserDropdown, {
    UserDropdownProps,
} from "web/react/components/app-header/pre-header-bar/user-dropdown";
import AutoCompleteProvider from "web/react/components/auto-complete/auto-complete.context";
import InstantFeedback from "web/react/components/instant-feedback/instant-feedback";
import { MainNavigationToggleMobile } from "web/react/components/main-navigation/main-navigation-toggle-mobile/main-navigation-toggle-mobile";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import { languageIsEnglish } from "web/script/modules/language";
import { AppHeaderBarSerializer } from "web/types/serializers";
import styles from "./header-bar.css";

interface HeaderBarProps {
    homeUrl: AppHeaderBarSerializer["home_url"];
    genders: AppHeaderBarSerializer["genders"];
    cartButton: AppHeaderBarSerializer["cart_button"];
    isAuthenticated: AppHeaderBarSerializer["is_authenticated"];
    mySearchesUrl: AppHeaderBarSerializer["my_searches_url"];
    savedItemUrl: AppHeaderBarSerializer["saved_item_url"];
    notificationsUrl: AppHeaderBarSerializer["notifications_url"];
    notificationsCount: AppHeaderBarSerializer["notifications_count"];
    searchInput: AppHeaderBarSerializer["search_input"];
    absoluteHomeUrl: AppHeaderBarSerializer["absolute_home_url"];
    absoluteActionUrl: AppHeaderBarSerializer["absolute_action_url"];
    userDropdown: UserDropdownProps;
    labels: Record<string, any>;
}

function HeaderBar({
    homeUrl,
    genders,
    cartButton,
    isAuthenticated,
    mySearchesUrl,
    savedItemUrl,
    notificationsUrl,
    notificationsCount,
    searchInput,
    userDropdown,
    labels,
    absoluteHomeUrl,
    absoluteActionUrl,
}: HeaderBarProps): React.ReactElement {
    const onClickSavedSearches = useClickTracker(
        "nav",
        "clicked",
        trim(mySearchesUrl as string, "/")
    );
    const onClickWishlist = useClickTracker("nav", "clicked", trim(savedItemUrl as string, "/"));
    const onClickAlerts = useClickTracker("icon", "clicked", trim(notificationsUrl as string, "/"));

    return (
        <div className={styles.headerBar}>
            <div className={styles.headerBarContainer}>
                <div className={styles.headerBarInner}>
                    <a className={classNames(styles.logo, "hidden-tablet")} href={homeUrl}>
                        <LystLogo />
                    </a>

                    <div className={styles.headerBarLeftContainer}>
                        <div className="hidden-desktop">
                            <MainNavigationToggleMobile />
                        </div>

                        <HeaderGenders genders={genders} />

                        <AutoCompleteProvider>
                            <>
                                <HeaderBarSearch {...searchInput} />
                                <JsonLd<WebSite>
                                    item={{
                                        "@context": "https://schema.org",
                                        "@type": "WebSite",
                                        url: absoluteHomeUrl,
                                        potentialAction: {
                                            "@type": "SearchAction",
                                            target: `${absoluteActionUrl}?q={q}`,
                                            "query-input": "name=q",
                                        },
                                    }}
                                />
                            </>
                        </AutoCompleteProvider>
                    </div>

                    <a className={classNames(styles.logo, "hidden-desktop")} href={homeUrl}>
                        <LystLogo />
                    </a>

                    {languageIsEnglish() ? (
                        <div className={styles.glyphsContainer}>
                            {isAuthenticated && (
                                <>
                                    <div className={classNames(styles.glyph, "hidden-tablet")}>
                                        <InstantFeedback
                                            message={labels["instant_feedback.du"]}
                                            area="saved-search"
                                            direction="bottom"
                                        >
                                            <a
                                                className={styles.icon}
                                                href={mySearchesUrl as string}
                                                onClick={onClickSavedSearches}
                                            >
                                                <div className={styles.iconLink}>
                                                    <SVGIcon name="searches-icon" />
                                                </div>
                                                <div className={styles.iconText}>
                                                    {
                                                        labels[
                                                            "saved_searches.header_icon_text"
                                                        ] /* My Searches */
                                                    }
                                                </div>
                                            </a>
                                        </InstantFeedback>
                                    </div>
                                    <div className={classNames(styles.glyph, "hidden-tablet")}>
                                        <InstantFeedback
                                            message={labels["instant_feedback.saved"]}
                                            area="heart"
                                            direction="bottom"
                                        >
                                            <a
                                                className={styles.icon}
                                                href={savedItemUrl as string}
                                                onClick={onClickWishlist}
                                            >
                                                <div
                                                    className={classNames(
                                                        styles.iconLink,
                                                        styles.noFill
                                                    )}
                                                >
                                                    <SVGIcon name="heart-icon-empty" />
                                                </div>
                                                <div className={styles.iconText}>
                                                    {labels["header.wishlist"] /* Wishlist */}
                                                </div>
                                            </a>
                                        </InstantFeedback>
                                    </div>
                                    <div className={classNames(styles.glyph, "hidden-tablet")}>
                                        <a
                                            className={styles.icon}
                                            href={notificationsUrl as string}
                                            onClick={onClickAlerts}
                                        >
                                            <div className={styles.iconLink}>
                                                <SVGIcon name="alerts-icon" />
                                                {notificationsCount && notificationsCount > 0 ? (
                                                    <span
                                                        className={classNames(
                                                            styles.notificationsCount,
                                                            {
                                                                [styles.notificationsCountSmall]:
                                                                    notificationsCount > 99,
                                                            }
                                                        )}
                                                    >
                                                        {notificationsCount}
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className={styles.iconText}>
                                                {labels["notifications.title"] /* Alerts */}
                                            </div>
                                        </a>
                                    </div>
                                </>
                            )}
                            <UserDropdown
                                className="hidden-desktop"
                                accountDropdown={userDropdown.accountDropdown}
                                accountText={userDropdown.accountText}
                                accountUrl={userDropdown.accountUrl}
                                registerUrl={userDropdown.registerUrl}
                            />

                            {cartButton && (
                                <CartSummary
                                    cartUrl={cartButton.cart_url}
                                    itemsCount={cartButton.items_count}
                                />
                            )}
                        </div>
                    ) : (
                        <div
                            className={classNames(styles.glyphsContainer, "hidden-desktop")}
                        /> /* maintain flex layout for non-english mobile header bar */
                    )}
                </div>
            </div>
        </div>
    );
}

export default HeaderBar;
